/** @jsx jsx */
import React, { useEffect, useState } from 'react';
import { Box, Container, Flex, Text, jsx } from 'theme-ui';
import { useStaticQuery, graphql } from 'gatsby';

import { Grid, Col, Row, Stack } from '../grid';
import { ButtonToggle } from '../button-toggle';

import { useCartLicenceCount } from '../../cart-provider';
import { BlockContent } from '../block-content';

import { ProductsList } from './products-list';
import { licenceVariants } from './constants';
import { Tally } from './tally';

import { TypefaceShopTotalSmall } from './total-small';

import { Dropdown, DropdownItem } from '../dropdown';

export const TypefaceShop = ({ typeface, data }) => {
  const { settings } = useStaticQuery(settingsQuery);

  const [tab, setTab] = useState('desktop');
  const [tier, setTier] = useState(0);
  const max = {
    desktop: data.maxDesktop,
    web: data.maxWeb,
    app: data.maxApp,
    ebook: data.maxEbook,
  };

  const maxVariant = parseInt(max[tab]);

  const variants = licenceVariants[tab].filter((v, idx) => idx <= maxVariant);

  const selectedVariant = variants[tier];

  const typefaceSlugs = data.rows.map((r) => r.slug.current);
  const count = useCartLicenceCount(typefaceSlugs);

  useEffect(() => {
    setTier(0);
  }, [tab]);

  return (
    <div id="shop">
      <Container
        sx={{
          display: 'none',
          bg: 'accent',
          position: 'sticky',
          top: 0,
          zIndex: 20,
          minHeight: [17],
          py: [7],
        }}
      >
        <Flex
          sx={{
            width: '100%',
            justifyContent: 'flex-end',
            alignItems: ['center'],
            color: 'txt.4',
            gap: [4],
          }}
        >
          {(data.promoMessage || data.promoSubtitle) && (
            <Stack gap={5}>
              {data.promoMessage && (
                <Text as="p" sx={{ fontWeight: 700, fontSize: 2, lineHeight: 1 }}>
                  {data.promoMessage}
                </Text>
              )}
              {data.promoSubtitle && (
                <Text as="p" sx={{ fontSize: 0, lineHeight: 1, pb: 3 }}>
                  {data.promoSubtitle}
                </Text>
              )}
            </Stack>
          )}

          <TypefaceShopTotalSmall typeface={typeface} footnote={settings.vat} />
        </Flex>
      </Container>

      <Container>
        <Row>
          <Stack variant="0">
            <Text as="h4" variant="section_header" id="shop">
              {data.shopTitle || 'Buying Options'}
            </Text>
            <Grid columns={[1, 12]}>
              <Col span={[1, 8, 8, 8, 6]}>
                <BlockContent blocks={data.shopInfo || settings.buying} />
              </Col>
            </Grid>
            <Stack gap={[6, 7]}>
              <div sx={{ bg: 'bg.0', py: [7], position: 'sticky', top: [17], zIndex: 20 }}>
                <Flex
                  sx={{
                    flexDirection: ['column', 'row'],
                    flex: 0,
                    width: '100%',
                    mb: [5, 0],
                    alignItems: ['flex-start', 'center'],
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      mb: [8, 0],
                      '& > *': {
                        display: 'inline-block',
                      },
                      '& > * + *': {
                        ml: [13, 13],
                      },
                    }}
                  >
                    {max.desktop && (
                      <ButtonToggle onClick={() => setTab('desktop')} pressed={tab === 'desktop'}>
                        Desktop <Tally tally={count.desktop} />
                      </ButtonToggle>
                    )}

                    {max.web && (
                      <ButtonToggle onClick={() => setTab('web')} pressed={tab === 'web'}>
                        Web <Tally tally={count.web} />
                      </ButtonToggle>
                    )}
                    {max.app && (
                      <ButtonToggle onClick={() => setTab('app')} pressed={tab === 'app'}>
                        App <Tally tally={count.app} />
                      </ButtonToggle>
                    )}
                    {max.ebook && (
                      <ButtonToggle onClick={() => setTab('ebook')} pressed={tab === 'ebook'}>
                        E-book
                        <Tally tally={count.ebook} />
                      </ButtonToggle>
                    )}
                  </Box>

                  <Box sx={{ flex: '0 1 auto' }}>
                    {variants.length === 1 ? (
                      <div
                        sx={{
                          display: 'none',
                          position: 'relative',
                          ':after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '0px',
                            left: 0,
                            width: '100%',
                            backgroundColor: 'txt.1',
                            height: '1px',
                          },
                        }}
                      >
                        {variants[0]}
                      </div>
                    ) : (
                      <Dropdown trigger={selectedVariant} disabled={variants.length === 1}>
                        {variants.map((variant, idx) => (
                          <DropdownItem value={idx} align="end" onSelect={setTier} key={variant}>
                            {variant}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    )}
                  </Box>
                </Flex>
              </div>
              <Stack gap={[6, 7]}>
                <ProductsList
                  licenceKey={tab}
                  licenceTier={tier}
                  variants={variants}
                  typeface={typeface}
                  products={data.rows}
                />
              </Stack>
            </Stack>
          </Stack>
        </Row>
      </Container>
    </div>
  );
};

const settingsQuery = graphql`
  query {
    settings: sanityShopSettings {
      vat
      buying: _rawBuying(resolveReferences: { maxDepth: 10 })
    }
  }
`;
